import { useEffect, useRef, useCallback } from 'react';

interface RecordingFABProps {
  status: 'idle' | 'initializing' | 'countdown' | 'recording' | 'paused' | 'completed';
  timer: number;
  formatTime: (secs: number) => string;
  recordingSizeBytesRef: React.RefObject<number>;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
}

// ── Thresholds ──
const TIME_GOOD = 3 * 60;      // 3 min — blue to green
const TIME_WARNING = 5 * 60;   // 5 min — green to yellow
const TIME_DANGER = 8 * 60;    // 8 min — yellow to red
const TIME_MAX = 10 * 60;      // 10 min — progress bar full

const SIZE_WARNING = 50 * 1024 * 1024;  // 50 MB — yellow pulse
const SIZE_DANGER = 100 * 1024 * 1024;  // 100 MB — red pulse

/**
 * RecordingFAB — Painel de status da gravação em janela popup separada.
 * Usa window.open() para NÃO ser capturado pelo getDisplayMedia.
 * Mostra: timer, progresso, tamanho, controles, atalhos.
 */
export function RecordingFAB({
  status, timer, formatTime, recordingSizeBytesRef,
  onPause, onResume, onStop
}: RecordingFABProps) {
  const popupRef = useRef<Window | null>(null);
  const cleanupDoneRef = useRef(false);

  const formatSize = useCallback((bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }, []);

  const getProgressColor = useCallback((seconds: number): string => {
    if (seconds < TIME_GOOD) return '#38bdf8';       // Blue — safe
    if (seconds < TIME_WARNING) return '#22c55e';     // Green — good
    if (seconds < TIME_DANGER) return '#eab308';      // Yellow — caution
    return '#ef4444';                                  // Red — too long
  }, []);

  const getProgressPercent = useCallback((seconds: number): number => {
    return Math.min(100, (seconds / TIME_MAX) * 100);
  }, []);

  const getSizeClass = useCallback((bytes: number): string => {
    if (bytes >= SIZE_DANGER) return 'size danger';
    if (bytes >= SIZE_WARNING) return 'size warning';
    return 'size';
  }, []);

  const getPopupHTML = useCallback(() => {
    return `<!DOCTYPE html>
<html>
<head>
  <title>ECAD Status</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
      background: #000000;
      color: #f8fafc;
      width: 100%;
      height: 100vh;
      display: flex;
      flex-direction: column;
      user-select: none;
      overflow: hidden;
    }

    /* ── Header ── */
    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 18px 32px;
      background: #050810;
      border-bottom: 1px solid rgba(56, 189, 248, 0.15);
    }
    .header-left {
      display: flex;
      align-items: center;
      gap: 12px;
    }
    .dot {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #ef4444;
      animation: pulse 1s infinite;
      flex-shrink: 0;
      box-shadow: 0 0 12px #ef4444, 0 0 24px rgba(239,68,68,0.4);
    }
    .dot.paused {
      background: #eab308;
      animation: pulse-slow 2s infinite;
      box-shadow: 0 0 12px #eab308, 0 0 24px rgba(234,179,8,0.4);
    }
    @keyframes pulse {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0.4; transform: scale(0.85); }
    }
    @keyframes pulse-slow {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
    .status-label {
      font-size: 22px;
      font-weight: 900;
      text-transform: uppercase;
      letter-spacing: 3px;
      color: #ef4444;
      text-shadow: 0 0 10px rgba(239,68,68,0.5);
    }
    .status-label.paused {
      color: #eab308;
      text-shadow: 0 0 10px rgba(234,179,8,0.5);
    }
    .header-badge {
      font-size: 16px;
      font-weight: 800;
      color: #64748b;
      background: #0f172a;
      padding: 6px 16px;
      border-radius: 12px;
      letter-spacing: 1px;
      border: 1px solid #1e293b;
    }

    /* ── Timer Section ── */
    .timer-section {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px 32px;
    }
    .timer {
      font-family: 'Consolas', 'Courier New', monospace;
      font-size: clamp(80px, 15vw, 180px);
      font-weight: 900;
      color: #38bdf8;
      text-shadow: 0 0 30px rgba(56, 189, 248, 0.5), 0 0 60px rgba(56, 189, 248, 0.2);
      letter-spacing: 8px;
      line-height: 1;
    }

    /* ── Progress Bar ── */
    .progress-section {
      padding: 0 32px 12px;
    }
    .progress-bar {
      width: 100%;
      height: 12px;
      background: #111827;
      border-radius: 6px;
      overflow: hidden;
      position: relative;
    }
    .progress-fill {
      height: 100%;
      border-radius: 6px;
      transition: width 1s linear, background-color 2s ease;
      box-shadow: 0 0 12px var(--bar-color), 0 0 24px var(--bar-color);
    }
    .progress-labels {
      display: flex;
      justify-content: space-between;
      margin-top: 6px;
      font-size: 14px;
      color: #475569;
      font-weight: 700;
    }

    /* ── Size ── */
    .size-section {
      text-align: center;
      padding: 8px 32px 12px;
    }
    .size {
      font-family: 'Consolas', 'Courier New', monospace;
      font-size: 32px;
      color: #64748b;
      font-weight: 800;
      transition: color 0.3s;
      text-shadow: 0 0 8px rgba(100,116,139,0.3);
    }
    .size.warning {
      color: #eab308;
      animation: size-pulse-warn 1.5s infinite;
      text-shadow: 0 0 12px rgba(234,179,8,0.5);
    }
    .size.danger {
      color: #ef4444;
      animation: size-pulse-danger 0.8s infinite;
      text-shadow: 0 0 15px rgba(239,68,68,0.5);
    }
    @keyframes size-pulse-warn {
      0%, 100% { color: #eab308; }
      50% { color: #ca8a04; }
    }
    @keyframes size-pulse-danger {
      0%, 100% { color: #ef4444; }
      50% { color: #f59e0b; }
    }
    .size-label {
      font-size: 14px;
      color: #334155;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 2px;
    }

    /* ── Divider ── */
    .divider {
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(56, 189, 248, 0.2), transparent);
      margin: 0 32px;
    }

    /* ── Controls ── */
    .controls {
      display: flex;
      gap: 16px;
      padding: 18px 32px;
    }
    .btn {
      flex: 1;
      padding: 16px 0;
      border: none;
      border-radius: 12px;
      font-size: 18px;
      font-weight: 800;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      transition: all 0.15s;
      letter-spacing: 0.5px;
    }
    .btn-pause {
      background: rgba(234, 179, 8, 0.12);
      border: 2px solid rgba(234, 179, 8, 0.3);
      color: #eab308;
    }
    .btn-pause:hover {
      background: rgba(234, 179, 8, 0.25);
      box-shadow: 0 0 20px rgba(234,179,8,0.2);
    }
    .btn-resume {
      background: rgba(56, 189, 248, 0.12);
      border: 2px solid rgba(56, 189, 248, 0.3);
      color: #38bdf8;
    }
    .btn-resume:hover {
      background: rgba(56, 189, 248, 0.25);
      box-shadow: 0 0 20px rgba(56,189,248,0.2);
    }
    .btn-stop {
      background: rgba(239, 68, 68, 0.12);
      border: 2px solid rgba(239, 68, 68, 0.3);
      color: #ef4444;
    }
    .btn-stop:hover {
      background: rgba(239, 68, 68, 0.3);
      box-shadow: 0 0 20px rgba(239,68,68,0.3);
    }

    /* ── Shortcuts Reference ── */
    .shortcuts {
      padding: 12px 32px 18px;
      display: flex;
      flex-wrap: wrap;
      gap: 8px 24px;
      overflow-y: auto;
    }
    .shortcuts-title {
      width: 100%;
      font-size: 13px;
      font-weight: 800;
      color: #334155;
      text-transform: uppercase;
      letter-spacing: 2px;
      margin-bottom: 4px;
    }
    .shortcut {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 14px;
      color: #94a3b8;
    }
    .shortcut .key {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 64px;
      padding: 4px 10px;
      background: #111827;
      border: 1px solid #1e293b;
      border-radius: 6px;
      font-size: 13px;
      font-weight: 800;
      color: #94a3b8;
      font-family: 'Consolas', monospace;
      text-align: center;
    }
    .shortcut .icon {
      width: 20px;
      text-align: center;
      font-size: 16px;
      flex-shrink: 0;
    }
    .shortcut .desc {
      font-size: 13px;
      color: #64748b;
    }
  </style>
</head>
<body>
  <!-- Header -->
  <div class="header">
    <div class="header-left">
      <span class="dot" id="dot"></span>
      <span class="status-label" id="statusLabel">GRAVANDO</span>
    </div>
    <span class="header-badge">${window.location.port}</span>
  </div>

  <!-- Timer -->
  <div class="timer-section">
    <div class="timer" id="timer">00:00</div>
  </div>

  <!-- Progress Bar -->
  <div class="progress-section">
    <div class="progress-bar">
      <div class="progress-fill" id="progressFill" style="width: 0%; background: #38bdf8; --bar-color: #38bdf8;"></div>
    </div>
    <div class="progress-labels">
      <span>0:00</span>
      <span>3:00</span>
      <span>5:00</span>
      <span>10:00</span>
    </div>
  </div>

  <!-- File Size -->
  <div class="size-section">
    <div class="size-label">Tamanho</div>
    <div class="size" id="sizeDisplay">0 B</div>
  </div>

  <div class="divider"></div>

  <!-- Controls -->
  <div class="controls">
    <button class="btn btn-pause" id="btnPause" onclick="window.opener?.postMessage({type:'ecad-fab',action:'pause'},'*')">
      <span>⏸</span> Pausar
    </button>
    <button class="btn btn-stop" id="btnStop" onclick="window.opener?.postMessage({type:'ecad-fab',action:'stop'},'*')">
      <span>■</span> Parar
    </button>
  </div>

  <div class="divider"></div>

  <!-- Shortcuts Reference -->
  <div class="shortcuts">
    <div class="shortcuts-title">Controles de Gravação</div>
    <div class="shortcut">
      <span class="key">Shift</span>
      <span class="icon">🔍</span>
      <span class="desc">Zoom no cursor</span>
    </div>
    <div class="shortcut">
      <span class="key">Ctrl</span>
      <span class="icon">💡</span>
      <span class="desc">Holofote (spotlight)</span>
    </div>
    <div class="shortcut">
      <span class="key">Dir ➜</span>
      <span class="icon">📌</span>
      <span class="desc">Arrastar spotlight fixo</span>
    </div>
    <div class="shortcut">
      <span class="key">Click</span>
      <span class="icon">✦</span>
      <span class="desc">Ripple + som de clique</span>
    </div>
    <div class="shortcut">
      <span class="key">ESC</span>
      <span class="icon">⏹</span>
      <span class="desc">Parar gravação</span>
    </div>
    <div class="shortcut">
      <span class="key">F10</span>
      <span class="icon">⏸</span>
      <span class="desc">Pausar / Continuar</span>
    </div>
    <div class="shortcut">
      <span class="key">F4</span>
      <span class="icon">📺</span>
      <span class="desc">Mostrar/Ocultar status</span>
    </div>
    <div class="shortcut">
      <span class="key">Ctrl+F9</span>
      <span class="icon">⏺</span>
      <span class="desc">Iniciar gravação</span>
    </div>
  </div>
</body>
</html>`;
  }, []);

  const openPopup = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();
      return;
    }

    cleanupDoneRef.current = false;
    const w = 280;
    const h = 480;

    let popLeft = window.screen.availWidth - w - 16;
    let popTop = 16;
    let popW = w;
    let popH = h;

    if (typeof window.screenLeft !== 'undefined' || typeof window.screenX !== 'undefined') {
      const mainLeft = window.screenLeft ?? window.screenX ?? 0;
      const mainScreen = window.screen;

      if (mainLeft < mainScreen.width) {
        popLeft = mainScreen.width;
        popTop = 0;
        popW = mainScreen.availWidth > mainScreen.width ? mainScreen.availWidth - mainScreen.width : mainScreen.width;
        popH = mainScreen.availHeight || 900;
      } else {
        popLeft = 0;
        popTop = 0;
        popW = mainScreen.width;
        popH = mainScreen.availHeight || 900;
      }
    }

    const popup = window.open(
      '',
      'ecad-recording-fab',
      `width=${popW},height=${popH},left=${popLeft},top=${popTop},toolbar=no,menubar=no,scrollbars=no,resizable=yes,location=no,status=no`
    );

    if (popup) {
      popup.document.open();
      popup.document.write(getPopupHTML());
      popup.document.close();
      popupRef.current = popup;

      // Focus back to main window so recording continues
      setTimeout(() => window.focus(), 200);
    }
  }, [getPopupHTML]);

  const togglePopup = useCallback(() => {
    const isRecordingOrPaused = status === 'recording' || status === 'paused';
    if (!isRecordingOrPaused) return;

    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
      popupRef.current = null;
    } else {
      openPopup();
    }
  }, [status, openPopup]);

  // Listen for postMessage from popup (controls)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'ecad-fab') return;
      switch (event.data.action) {
        case 'pause': onPause(); break;
        case 'resume': onResume(); break;
        case 'stop': onStop(); break;
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onPause, onResume, onStop]);

  // Listen for custom toggle trigger (e.g. F4 key)
  useEffect(() => {
    const handleToggle = () => {
      togglePopup();
    };
    window.addEventListener('ecad-toggle-status-popup', handleToggle);
    return () => {
      window.removeEventListener('ecad-toggle-status-popup', handleToggle);
    };
  }, [togglePopup]);

  // Update popup content every second (timer drives this via prop change)
  useEffect(() => {
    const popup = popupRef.current;
    if (!popup || popup.closed) return;
    if (status !== 'recording' && status !== 'paused') return;

    try {
      const doc = popup.document;

      // Timer
      const timerEl = doc.getElementById('timer');
      if (timerEl) {
        timerEl.textContent = formatTime(timer);
        timerEl.style.color = getProgressColor(timer);
        timerEl.style.textShadow = `0 0 20px ${getProgressColor(timer)}44`;
      }

      // Progress bar
      const progressFill = doc.getElementById('progressFill');
      if (progressFill) {
        const color = getProgressColor(timer);
        progressFill.style.width = `${getProgressPercent(timer)}%`;
        progressFill.style.background = color;
        progressFill.style.setProperty('--bar-color', color);
      }

      // File size
      const sizeDisplay = doc.getElementById('sizeDisplay');
      if (sizeDisplay) {
        const bytes = recordingSizeBytesRef.current ?? 0;
        sizeDisplay.textContent = formatSize(bytes);
        sizeDisplay.className = getSizeClass(bytes);
      }

      // Status dot & label
      const dotEl = doc.getElementById('dot');
      const statusLabel = doc.getElementById('statusLabel');
      const isPaused = status === 'paused';

      if (dotEl) dotEl.className = isPaused ? 'dot paused' : 'dot';
      if (statusLabel) {
        statusLabel.textContent = isPaused ? 'PAUSADO' : 'GRAVANDO';
        statusLabel.className = isPaused ? 'status-label paused' : 'status-label';
      }

      // Swap pause/resume button
      const btnPause = doc.getElementById('btnPause');
      if (btnPause) {
        if (isPaused) {
          btnPause.className = 'btn btn-resume';
          btnPause.innerHTML = '<span>▶</span> Continuar';
          btnPause.setAttribute('onclick', "window.opener?.postMessage({type:'ecad-fab',action:'resume'},'*')");
        } else {
          btnPause.className = 'btn btn-pause';
          btnPause.innerHTML = '<span>⏸</span> Pausar';
          btnPause.setAttribute('onclick', "window.opener?.postMessage({type:'ecad-fab',action:'pause'},'*')");
        }
      }
    } catch { /* popup closed */ }
  }, [timer, status, formatTime, formatSize, getProgressColor, getProgressPercent, getSizeClass, recordingSizeBytesRef]);

  // Close popup when recording ends
  useEffect(() => {
    if (status !== 'recording' && status !== 'paused' && !cleanupDoneRef.current) {
      cleanupDoneRef.current = true;
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
      popupRef.current = null;
    }
  }, [status]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
      popupRef.current = null;
    };
  }, []);

  // Renders nothing — all UI is in the popup
  return null;
}
